import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  inject,
  DestroyRef,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import {
  MatLegacyAutocomplete as MatAutocomplete,
  MatLegacyAutocompleteModule as MatAutocompleteModule,
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';

import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs';
import Quill from 'quill';
import { QuillModule } from 'ngx-quill';
import { TsButtonModule, TSIconModule } from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { MentionModule, MentionConfig } from 'angular-mentions';
import { User } from '@core/models';
import { AccountService } from 'app/core';
import { Note } from '@shared/modules/notes/notes.types';
import { Contact } from '@modules/contacts/contacts.types';
import { Company } from '@modules/companies/companies.types';
import { NotesService } from '@shared/modules/notes/notes.service';
import { MentionsService } from '@shared/services/mentions.service';
import { ContactsService } from '@modules/contacts/contacts.service';
import { MSWORD_MATCHERS, QUILL_MODULES } from '@shared/Utils/quill-editor.utils';

declare interface DialogData {
  contact: Contact;
  company: Company;
  note: Note;
}

@Component({
  selector: 'company-contact-add-note-dialog',
  templateUrl: './add-note.component.html',
  styleUrls: ['./add-note.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TsDialogComponent,
    TsButtonModule,
    TSIconModule,
    MatInputModule,
    MatChipsModule,
    MatAutocompleteModule,
    MentionModule,
    QuillModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyAndContactAddNoteDialogComponent {
  noteForm: UntypedFormGroup;
  contactList: Contact[] = [];
  separatorKeysCodes: number[] = [ENTER];
  contactSearchControl = new UntypedFormControl();
  selectedUsers: any[] = [];
  quill: Quill;
  quillModules = QUILL_MODULES;
  public mentionConfig: MentionConfig;

  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('userInput') userInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  get company(): Company {
    return this.data?.company;
  }

  get contact(): Contact {
    return this.data?.contact;
  }

  get note(): Note {
    return this.data?.note;
  }

  get isEditNote(): boolean {
    return this.data?.note ? true : false;
  }

  get dynamicDialogTitle(): string {
    return this.isEditNote ? 'Edit Note' : 'Add Note';
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: DialogData,
    private readonly cdRef: ChangeDetectorRef,
    private readonly snackBar: MatSnackBar,
    private readonly untypedFormBuilder: UntypedFormBuilder,
    private readonly matDialogRef: MatDialogRef<CompanyAndContactAddNoteDialogComponent>,
    private readonly accountService: AccountService,
    private readonly contactsService: ContactsService,
    private readonly notesService: NotesService,
    private readonly mentionsService: MentionsService,
  ) {}

  ngOnInit(): void {
    this.contactsService.accountId = this.accountService.getCurrentAccount()?.id;
    this.buildForm();
    this.mentionConfig = this.mentionsService.getDefaultMentionConfig();
    this.watchAssociateContactsInputChanges();
  }

  private buildForm(): void {
    this.noteForm = this.untypedFormBuilder.group({
      title: [this.data.note?.title ? this.data.note.title : '', [Validators.required]],
      body: [this.data.note?.body ? this.data.note.body : '', [Validators.required]],
    });
    this.selectedUsers = this.data.note?.contacts || [];
  }

  private watchAssociateContactsInputChanges(): void {
    this.contactSearchControl.valueChanges
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef), debounceTime(400), distinctUntilChanged())
      .subscribe((query: string) => {
        this.contactsService
          .getContacts(1, 20, 'first_name', 'asc', '', { name: query })
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            map(({ contacts }) => contacts),
          )
          .subscribe((contacts) => {
            this.contactList = contacts;
            this.cdRef.markForCheck();
          });
      });
  }

  /**
   * Get the editor instance and add the MS Word matchers.
   */
  onEditorCreated(quill: Quill): void {
    this.quill = quill;
    const clipboard = this.quill.getModule('clipboard') as any;

    MSWORD_MATCHERS.forEach(([selector, matcher]) => {
      clipboard.addMatcher(selector, matcher);
    });
  }

  isFormDisabled(): boolean {
    return this.isEditNote ? false : this.noteForm.pristine || this.noteForm.invalid || this.noteForm.disabled;
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectedUsers.push({
      id: event.option.id,
      name: event.option.value,
    });
    this.userInput.nativeElement.value = '';
    this.contactSearchControl.reset();
  }

  removeUser(user: User): void {
    this.selectedUsers.splice(
      this.selectedUsers.findIndex((item) => item.id === user.id),
      1,
    );
    this.cdRef.markForCheck();
  }

  discard(): void {
    this.matDialogRef.close();
  }

  sendAndClose(): void {
    this.noteForm.disable();
    const noteForm = this.noteForm.getRawValue();
    const note = {
      body: this.mentionsService.transformMentionsForBackend(noteForm.body),
      title: noteForm.title ? noteForm.title : this.note?.title,
      contact_ids: this.isEditNote
        ? this.selectedUsers.map((user) => user.id)
        : this.company
          ? this.selectedUsers.map((user) => user.id)
          : [Number(this.contact.id)],
      noteable_id: this.isEditNote
        ? this.note.noteable_id
        : this.company
          ? parseInt(this.company.id)
          : parseInt(this.contact.id),
      noteable_type: this.isEditNote ? this.note.noteable_type : this.company ? 'Company' : 'Contact',
      id: this.isEditNote ? this.note.id : null,
    };

    const successMessage = `Successfully ${this.isEditNote ? 'updated' : 'created'} note.`;
    const errorMessage = `Unable to ${
      this.isEditNote ? 'update' : 'create'
    } note at the moment. Please try again later.`;

    const request = this.isEditNote ? this.notesService.updateNote(note) : this.notesService.createNote(note);

    const triggerSnackBarOnDialogClose = (message: string, event: 'success' | 'error', note?: Note) => {
      this.snackBar.open(message, null, {
        duration: 3000,
        panelClass: event === 'success' ? 'success-snackbar' : 'error-snackbar',
      });
      this.matDialogRef.close(note);
    };

    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (note) => triggerSnackBarOnDialogClose(successMessage, 'success', note),
      error: () => triggerSnackBarOnDialogClose(errorMessage, 'error'),
    });
  }
}
